import Delete from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { Exercise } from "../../../src/types/contents/exercise";
import type { Day } from "../../../src/types/contents/programDay";
import type { Workout } from "../../../src/types/contents/workout";
import {
  createProgram,
  getContentList,
  getExercise,
  getProgram,
  getWorkout,
  updateProgram,
  uploadMedia,
} from "../api";
import { auth } from "../auth";
import type { ApiProgram } from "../types";

import { MediaWithPreview, TagsSelector } from "./Common";
import { MultiSelect } from "./MultiSelect";

const emptyProgram: ApiProgram = {
  title: "",
  description: "",
  category: "",
  language: "",
  numberOfWeeks: 0,
  skillLevel: 0,
  objective1: "",
  objective2: "",
  load: 0,
  days: [],
  diet: [],
  tags: [],
  favorite: false,
};

const isWorkoutDay = (
  day: Day<"rest" | "workouts">,
): day is Day<"workouts"> => {
  return day.type === "workouts";
};

const Days = ({
  days,
  onChange,
}: {
  days: Day<"rest" | "workouts">[];
  onChange: (days: Day<"rest" | "workouts">[]) => void;
}) => {
  const [workouts, setWorkouts] = useState<Workout[]>([]);

  const options = workouts.map(({ title, id }) => ({
    label: title,
    value: id.toString(),
  }));

  useEffect(() => {
    const init = async () => {
      setWorkouts(await getContentList<Workout>("workout"));
    };

    if (auth.currentUser) void init();
  }, []);

  const setWorkout = async (workoutId: string, dayIndex: number) => {
    const workout = await getWorkout(workoutId);
    if (workout) {
      const updatedDays = [...days];
      const day = updatedDays[dayIndex];
      if (isWorkoutDay(day)) {
        updatedDays[dayIndex] = {
          ...day,
          workouts: [...day.workouts, workout],
        };
        onChange(updatedDays);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Days:</Typography>
      {days.map((day, index) => {
        if (day.type === "rest") {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              key={index}
            >
              <Typography key={index} variant="body1">
                {index + 1}: Rest
              </Typography>
              <IconButton
                onClick={() => {
                  onChange(days.filter((_, i) => i !== index));
                }}
              >
                <Delete />
              </IconButton>
            </Stack>
          );
        } else {
          const workoutIds = day.workouts.map((w) => w.id.toString());
          return (
            <Stack key={index} spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                key={index}
              >
                <Typography variant="body1">
                  {index + 1}: Workouts ({day.workouts.length})
                </Typography>
                <IconButton
                  onClick={() => {
                    onChange(days.filter((_, i) => i !== index));
                  }}
                >
                  <Delete />
                </IconButton>
              </Stack>
              <MultiSelect
                label="Workouts"
                options={options}
                value={workoutIds}
                onChange={async (selected) => {
                  const newlySelected = selected.find(
                    (workoutId) => !workoutIds.includes(workoutId),
                  );
                  if (newlySelected) {
                    void setWorkout(newlySelected, index);
                  }
                }}
              />
            </Stack>
          );
        }
      })}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={() => onChange([...days, { type: "rest" }])}
        >
          Add rest day
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onChange([...days, { type: "workouts", workouts: [] }])
          }
        >
          Add workout day
        </Button>
      </Stack>
    </Stack>
  );
};

export const ProgramComponent = () => {
  const [program, setProgram] = useState<ApiProgram | null>(null);
  const [mediaPreview, setMediaPreview] = useState<File | null>(null);
  const [mediaIdOverride, setMediaIdOverride] = useState<boolean>(false);

  const { id } = useParams();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (isEdit) {
        setProgram(await getProgram(id));
      } else {
        setProgram({
          ...emptyProgram,
        });
      }
    };

    if (auth.currentUser) void init();
  }, []);

  if (program === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">Loading...</Typography>
      </Stack>
    );
  }

  const upsertMedia = async () => {
    if (mediaPreview) {
      const uploadResult = await uploadMedia(
        mediaPreview,
        mediaIdOverride ? program.media : undefined,
      );
      setMediaPreview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return program.media;
  };

  const save = async () => {
    try {
      const media = await upsertMedia();
      const programData: ApiProgram & { favorite: boolean } = {
        ...program,
        media,
      };
      if (isEdit) {
        await updateProgram(programData);
      } else {
        const { id: newId } = await createProgram(programData);
        if (newId) {
          navigate(`/program/${newId}`);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        alert(e.message);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Title"
        value={program.title}
        variant="outlined"
        onChange={(input) => {
          setProgram({ ...program, title: input.currentTarget.value });
        }}
      />
      <TextField
        label="Description"
        value={program.description}
        variant="outlined"
        onChange={(input) => {
          setProgram({
            ...program,
            description: input.currentTarget.value,
          });
        }}
      />
      <TextField
        label="Category"
        value={program.category}
        variant="outlined"
        onChange={(input) => {
          setProgram({ ...program, category: input.currentTarget.value });
        }}
      />
      <TextField
        label="Language"
        value={program.language}
        variant="outlined"
        onChange={(input) => {
          setProgram({ ...program, language: input.currentTarget.value });
        }}
      />
      <TextField
        label="Objective"
        value={program.objective1}
        variant="outlined"
        onChange={(input) => {
          setProgram({ ...program, objective1: input.currentTarget.value });
        }}
      />
      <TextField
        label="Secondary objective"
        value={program.objective2}
        variant="outlined"
        onChange={(input) => {
          setProgram({ ...program, objective2: input.currentTarget.value });
        }}
      />
      <TextField
        type="number"
        value={program.numberOfWeeks}
        label="Number of weeks"
        variant="outlined"
        onChange={(input) => {
          setProgram({
            ...program,
            numberOfWeeks: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={program.skillLevel}
        label="Skill level"
        variant="outlined"
        onChange={(input) => {
          setProgram({
            ...program,
            skillLevel: parseInt(input.currentTarget.value),
          });
        }}
      />
      <TextField
        type="number"
        value={program.load}
        label="Load"
        variant="outlined"
        onChange={(input) => {
          setProgram({
            ...program,
            load: parseInt(input.currentTarget.value),
          });
        }}
      />
      <Days
        days={program.days}
        onChange={(days: Day<"rest" | "workouts">[]) => {
          setProgram({
            ...program,
            days,
          });
        }}
      ></Days>
      <MediaWithPreview
        type="image"
        label="Upload media"
        alt="Exercise media"
        mediaId={program.media}
        mediaPreview={mediaPreview}
        setMediaPreview={setMediaPreview}
        mediaIdOverride={mediaIdOverride}
        setMediaIdOverride={setMediaIdOverride}
        overrideAble={isEdit}
      />
      <Stack direction="row" spacing={2}>
        <TagsSelector
          value={program.tags ?? []}
          onChange={(selected) => {
            setProgram({
              ...program,
              tags: selected,
            });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={program.favorite}
              onChange={(e) =>
                setProgram({ ...program, favorite: e.target.checked })
              }
            />
          }
          label="Favorite"
        />
      </Stack>
      <Button variant="contained" onClick={() => save()}>
        {program.id ? "Save" : "Create"}
      </Button>
    </Stack>
  );
};
